import { useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import ToggleButton from "core/buttons/toggle-button/ToggleButton";
import Button from "core/buttons/button/Button";
import StepHeader from "./StepHeader";
import QualifForm from "../QualifForm";
import QualifReviewCard from "../QualifReviewCard";
import { useNeedContext } from "api/mission/need/NeedProvider";
import { useJobContext } from "api/settings/job/JobProvider";
import { useJobCategoryContext } from "api/settings/job-category/JobCategoryProvider";
import { QUERY_STATUS } from "constants/constants";
import { emptyNeed } from "utils/fafHelper";

export const emptyNewQualif = () => ({
  // Stable React key (see emptyNeed): prevents a mid-list delete from bleeding a sibling's
  // in-progress child state (e.g. an inline need being created) onto the surviving card.
  uid: crypto.randomUUID(),
  candidate_dyb_id: null,
  candidate_first_name: "",
  candidate_last_name: "",
  candidate_email: "",
  qualification_date: "",
  use_new_need: false,
  need_creation_forced: false,
  need_id: null,
  new_need: emptyNeed(),
});

// Step 6 — qualifications: review the in-progress qualifs (status banner via qualif-review,
// see QualifReviewCard) + create a new qualification (without status, cf. back).
const QualificationsStep = ({
  reviewQualifs,
  statusMap,
  setQualifStatus,
  showNewQualif,
  setShowNewQualif,
  newQualifs,
  setNewQualifs,
  qualifReviewError,
  newQualifErrors,
  needBudgetReached,
  lang,
}) => {
  // Qualifs to review: list filtered by the wizard (excluding qualifs from the
  // current declaration, which are handled as "being added").
  const { activeNeeds, getActiveNeeds, loadStatus } = useNeedContext();
  const { jobListGetter } = useJobContext();
  const { jobCategoryListGetter } = useJobCategoryContext();

  useEffect(() => {
    getActiveNeeds(); // "besoin concerné" select: only active needs
  }, [getActiveNeeds]);

  // Both referentials are required by the nested NeedForm (on-the-fly need
  // creation): the category select narrows the job select, and it is the job
  // that the need carries (redmine 12940).
  // Loaded apart from the needs: `jobListGetter` changes identity as soon as
  // the job list resolves, so sharing an effect would fire a second GET /need.
  useEffect(() => {
    jobListGetter();
  }, [jobListGetter]);

  useEffect(() => {
    jobCategoryListGetter();
  }, [jobCategoryListGetter]);

  // No attachable active need (list loaded AND empty): the qualif cannot reference
  // an existing need → each new qualif switches to need creation (cf. QualifForm).
  const noActiveNeeds =
    loadStatus === QUERY_STATUS.SUCCESS && activeNeeds.length === 0;

  const needTitle = (needId) => activeNeeds.find((n) => n.id === needId)?.title;

  const updateNewQualif = (index, patch) =>
    setNewQualifs((prev) =>
      prev.map((q, i) => (i === index ? { ...q, ...patch } : q))
    );

  // Removes an added qualif (e.g. by accident); if it was the last one, closes the section.
  const removeQualif = (index) => {
    const next = newQualifs.filter((_, i) => i !== index);
    setNewQualifs(next);
    if (next.length === 0) setShowNewQualif(false);
  };

  return (
    <div className="faf-step">
      <StepHeader>
        <FormattedMessage
          id="tools_manager_faf_qualifications_title"
          defaultMessage="Où en sont tes qualifications ?"
        />
      </StepHeader>

      {reviewQualifs.map(({ qualif }) => (
        <QualifReviewCard
          key={qualif.id}
          qualif={qualif}
          needTitle={needTitle(qualif.need_id)}
          review={statusMap[qualif.id] || {}}
          onChange={(patch) => setQualifStatus(qualif.id, patch)}
          showError={qualifReviewError}
          lang={lang}
        />
      ))}

      <div className="faf-add-need-row">
        <span className="faf-field-label">
          <FormattedMessage
            id="tools_manager_faf_qualif_add_question"
            defaultMessage="Ajouter une nouvelle qualification ?"
          />
        </span>
        <ToggleButton
          id="faf-add-qualif"
          dataTestId="faf-qualif-add-toggle"
          value={showNewQualif}
          onChange={(v) => {
            setShowNewQualif(v);
            if (v && newQualifs.length === 0) setNewQualifs([emptyNewQualif()]);
          }}
        />
      </div>

      {showNewQualif &&
        newQualifs.map((q, i) => (
          <QualifForm
            key={q.uid ?? i}
            id={i}
            qualif={q}
            needs={activeNeeds}
            onChange={(patch) => updateNewQualif(i, patch)}
            showErrors={newQualifErrors?.[i]}
            lang={lang}
            needBudgetReached={needBudgetReached}
            noActiveNeeds={noActiveNeeds}
            onRemove={() => removeQualif(i)}
          />
        ))}

      {showNewQualif && (
        <Button
          size="medium"
          color="deep-purple"
          data-testid="faf-qualif-add-another"
          onClick={() => setNewQualifs((prev) => [...prev, emptyNewQualif()])}
        >
          <FormattedMessage
            id="tools_manager_faf_qualif_add_another"
            defaultMessage="Ajouter une autre qualification"
          />
        </Button>
      )}
    </div>
  );
};

QualificationsStep.propTypes = {
  reviewQualifs: PropTypes.array.isRequired,
  statusMap: PropTypes.object.isRequired,
  setQualifStatus: PropTypes.func.isRequired,
  showNewQualif: PropTypes.bool.isRequired,
  setShowNewQualif: PropTypes.func.isRequired,
  newQualifs: PropTypes.array.isRequired,
  setNewQualifs: PropTypes.func.isRequired,
  qualifReviewError: PropTypes.bool,
  newQualifErrors: PropTypes.array,
  needBudgetReached: PropTypes.bool,
  lang: PropTypes.string,
};

export default QualificationsStep;
